import React, { useState } from 'react';
import { AppBar, Toolbar, Typography, Button, Box, IconButton, Drawer, List, ListItem, ListItemText, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/system';
import './App.css';
import { Link } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu'; 
import ScrollLine from './ScrollLine';

const StyledButton = styled(Button)(() => ({
  fontFamily: 'DM Sans, sans-serif',
  textTransform: 'none',
  fontSize: 'clamp(1rem, 0.9vw, 1rem)',   // shrinks as viewport gets smaller/zoomed
  padding: 'clamp(4px, 0.6vw, 8px) clamp(6px, 0.6vw, 12px)',
  whiteSpace: 'nowrap',
  '&:hover': {
    textDecoration: 'underline',
    transition: 'text-decoration 0.3s',
    backgroundColor: 'transparent',
  },
}));

const handleClick = () => {
    window.scrollTo(0, 0);  // Scrolls to the top left corner of the page
};

const BookNowButton = styled(Button)(() => ({
  fontFamily: 'DM Sans, sans-serif',
  fontWeight: 700,
  fontSize: 'clamp(0.8rem, 0.9vw, 0.95rem)',
  padding: 'clamp(6px, 0.8vw, 10px) clamp(14px, 1.5vw, 20px)', // ⬅️ More left/right space
  textTransform: 'none',
  background: 'linear-gradient(135deg, #006699 0%, #006699 100%)',
  color: 'white',
  borderRadius: '30px',
  boxShadow: '0 3px 10px rgba(0, 140, 255, 0.4)',
  transition: 'all 0.3s ease',
  letterSpacing: '0.03em',
  whiteSpace: 'nowrap',
  maxWidth: '220px',
  '&:hover': {
    background: 'linear-gradient(135deg, #006699 0%, #006699 100%)',
    boxShadow: '0 5px 15px rgba(0, 123, 255, 0.6)',
    transform: 'scale(1.05)',
  },
  '&:active': {
    transform: 'scale(0.98)',
  },
}));

const ResponsiveAppBar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);  // This will close the menu
    window.scrollTo(0, 0);  
  };

  return (
    <AppBar 
      position="sticky" 
      sx={{ 
        backgroundColor: '#2049A3', 
        borderBottom: '5px solid transparent',  
        backgroundImage: `linear-gradient(to top, #2049A3, #2049A3), url("data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22400%22 height=%2240%22 viewBox=%220 0 400 40%22%3E%3Cpath d=%22M0,0 Q50,15 100,0 T200,0 T300,0 T400,0 L400,40 L0,40 Z%22 fill=%22%2366BB6A%22/%3E%3C/svg%3E")`

      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', minHeight: { xs: 64, sm: 80 }, overflow: 'hidden' }}>
        {/* Logo and Company Name Section */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Link to="/" onClick={handleClick} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
            <IconButton edge="start" color="inherit" sx={{ ml: 2 }}>
              <img src="/cleanlogo.png" alt="Logo" style={{ height: 60, width:150 }} />
            </IconButton>
          </Link>
        </Box>

        {/* Menu Button (Hamburger icon) for small screens */}
        <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
          <IconButton edge="end" color="inherit" onClick={toggleMobileMenu}>
            <MenuIcon />
          </IconButton>
        </Box>

        {/* Menu links (visible on larger screens) */}
        <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 1.5 }}>
          <Link to="/" onClick={closeMobileMenu}>
            <StyledButton sx={{
              ml: 2,
              color: 'white',  // Default for larger screens
              borderColor: 'white',  // Default for larger screens
              '&:hover': {
                color: 'white',
                borderColor: 'white'
              },
              // For smaller screens (under 600px) set black color and border
              '@media (max-width:600px)': {
                color: 'black',
                borderColor: 'black',
              }
            }}>Home</StyledButton>
          </Link>
          <Link to="/pricing" onClick={closeMobileMenu}>
            <StyledButton sx={{
              ml: 2,
              color: 'white',  // Default for larger screens
              borderColor: 'white',  // Default for larger screens
              '&:hover': {
                color: 'white',
                borderColor: 'white'
              },
              // For smaller screens (under 600px) set black color and border
              '@media (max-width:600px)': {
                color: 'black',
                borderColor: 'black',
              }
            }}>Services & Pricing</StyledButton>
          </Link>
          <Link to="/services" onClick={closeMobileMenu}>
            <StyledButton sx={{
              ml: 2,
              color: 'white',  // Default for larger screens
              borderColor: 'white',  // Default for larger screens
              '&:hover': {
                color: 'white',
                borderColor: 'white'
              },
              // For smaller screens (under 600px) set black color and border
              '@media (max-width:600px)': {
                color: 'black',
                borderColor: 'black',
              }
            }}>Why Choose Us</StyledButton>
          </Link>
          <Link to="/about-us" onClick={closeMobileMenu}>
            <StyledButton sx={{
              ml: 2,
              color: 'white',  // Default for larger screens
              borderColor: 'white',  // Default for larger screens
              '&:hover': {
                color: 'white',
                borderColor: 'white'
              },
              // For smaller screens (under 600px) set black color and border
              '@media (max-width:600px)': {
                color: 'black',
                borderColor: 'black',
              }
            }}>About Us</StyledButton>
          </Link>
          <Link to="/contact-us" onClick={closeMobileMenu}>
            <StyledButton sx={{
              ml: 2,
              color: 'white',  // Default for larger screens
              borderColor: 'white',  // Default for larger screens
              '&:hover': {
                color: 'white',
                borderColor: 'white'
              },
              // For smaller screens (under 600px) set black color and border
              '@media (max-width:600px)': {
                color: 'black',
                borderColor: 'black',
              }
            }}>Contact Us</StyledButton>
          </Link>

          <Link to="/contact-us" style={{ textDecoration: 'none' }} onClick={closeMobileMenu}>
            <Button
                            variant="contained"
                            href="/contact-us"
                            sx={{
                              ml: 2,
                              backgroundColor: '#006699',
                              borderRadius: 30,
                              px: { xs: 2, md: 3 },   // smaller on zoom / small screens
                              py: { xs: 1, md: 1.2 },
                              fontSize: 'clamp(0.9rem, 0.8vw, 0.9rem)',
                              maxWidth: '140px',
                              whiteSpace: 'nowrap',
                              fontWeight: 600,
                              textTransform: 'none',
                              transition: 'transform 0.3s ease-in-out',
                              '&:hover': {
                                backgroundColor: '#006699',
                                transform: 'scale(1.05)',
                              },
                            }}
                          >
                            Get a Quote
                          </Button>
          </Link>
          <a href="tel:+61403600961" style={{ textDecoration: 'none' }}>
            <Button
                            variant="contained"
                            sx={{
                              backgroundColor: '#ffffffff',
                              color:'black',
                              ml:2,
                              borderRadius: 30,
                              px: { xs: 2, md: 3 },   // smaller on zoom / small screens
                              py: { xs: 1, md: 1.2 },
                              fontSize: 'clamp(0.9rem, 0.8vw, 0.9rem)',
                              whiteSpace: 'nowrap',
                              maxWidth: '140px',
                              fontWeight: 600,
                              textTransform: 'none',
                              transition: 'transform 0.3s ease-in-out',
                              '&:hover': {
                                backgroundColor: '#ffffffff',
                                transform: 'scale(1.05)',
                              },
                            }}
                          >
                            Call Now
                          </Button>
          </a>
        </Box>
      </Toolbar>

      {/* Mobile Menu (Drawer) */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={toggleMobileMenu}
        sx={{ display: { xs: 'block', md: 'none' } }}
      >
        <List sx={{ width: 250, padding: '20px 0' }}>
          <ListItem button onClick={closeMobileMenu}>
            <Link to="/pricing" onClick={handleClick} style={{ textDecoration: 'none' }}>
              <StyledButton fullWidth sx={{
                mb: 2,
                color: 'black',  // Default for mobile
                borderColor: 'black',
                '&:hover': {
                  color: 'black',
                  borderColor: 'black',
                },
              }}>Services & Pricing</StyledButton>
            </Link>
          </ListItem>
          <ListItem button onClick={closeMobileMenu}>
            <Link to="/about-us" style={{ textDecoration: 'none' }}>
              <StyledButton fullWidth sx={{
                mb: 2,
                color: 'black',  // Default for mobile
                borderColor: 'black',
                '&:hover': {
                  color: 'black',
                  borderColor: 'black',
                },
              }}>About Us</StyledButton>
            </Link>
          </ListItem>
          <ListItem button onClick={closeMobileMenu}>
            <Link to="/contact-us" style={{ textDecoration: 'none' }}>
              <StyledButton fullWidth sx={{
                mb: 2,
                color: 'black',  // Default for mobile
                borderColor: 'black',
                '&:hover': {
                  color: 'black',
                  borderColor: 'black',
                },
              }}>Contact Us</StyledButton>
            </Link>
          </ListItem>
          <ListItem button onClick={closeMobileMenu}>
            <Link to="/contact-us">
  <BookNowButton>
    Get a Quote
  </BookNowButton>
</Link>

          </ListItem>
          
          <ListItem button>
            <a href="tel:+61403600961" style={{ textDecoration: 'none' }}>
              <StyledButton fullWidth sx={{
                mb: 2,
                fontWeight: 'bold',
                color: 'black',  // Default for mobile
                borderColor: 'black',
                '&:hover': {
                  color: 'black',
                  borderColor: 'black',
                },
              }}>Call Now</StyledButton>
            </a>
          </ListItem>
        </List>
      </Drawer>
      <ScrollLine/>
    </AppBar>
  );
};

export default ResponsiveAppBar;
